import type { OperationType } from '@/shared/types';
import { DepositCryptoModal } from '@/features/deposit-crypto';
import { DepositInstructionModal } from '@/features/deposit-instruction';
import { DepositBankTransferModal } from '@/features/deposit-bank-transfer';
import { DepositCardModal } from '@/features/deposit-card';
import { DepositSbpModal } from '@/features/deposit-sbp';
import { DepositSberpayModal } from '@/features/deposit-sberpay';
import { DepositSccrsModal } from '@/features/deposit-sccrs';
import { OperationMethodsModal } from '@/widgets';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type DialogComponentOptions, useModalStore } from '@/shared/lib';
import { useWithdrawCryptoModals } from '@/features/withdraw-crypto/lib';
import { useWithdrawCardModals } from '@/features/withdraw-card';
import { useWithdrawBankTransferModals } from '@/features/withdraw-bank-transfer';
import { useWithdrawSbpModals } from '@/features/withdraw-sbp';
import type { OperationMethod } from './types';
import type { AssetGroup } from '@/entities/account';
import { useVerificationInfoModal } from '@/entities/verification';
import { useSessionStore } from '@/entities/session';
import { useWithdrawSccrsModals } from '@/features/withdraw-sccrs';
import { useDepositWithdrawAssetsDictionary } from '@/shared/api';
import { hasOperationMethodData } from '../lib';
import { OperationMethodDataGate } from '../ui/OperationMethodDataGate';

interface UseOperationMethodsOptions {
  operationType: OperationType;
  onMethodSelect?: (method: OperationMethod) => void;
  modalClassName?: string;
}

export type OpenOperationMethodsModalProps = {
  assetGroup?: AssetGroup;
  assetCode?: string;
  customOnMethodSelect?: (method: OperationMethod) => void;
  onClose?: () => void;
};

export function useOperationMethods(options: UseOperationMethodsOptions) {
  const {
    operationType,
    onMethodSelect: customOnMethodSelect,
    modalClassName,
  } = options;
  const { t } = useTranslation();
  const { session } = useSessionStore();

  const { showDialogComponent, closeDialog } = useModalStore();
  const { openVerificationInfoModal } = useVerificationInfoModal();
  const { openWithdrawFormModal } = useWithdrawCryptoModals();
  const { openWithdrawCardModal } = useWithdrawCardModals();
  const { openWithdrawBankTransferModal } = useWithdrawBankTransferModals();
  const { openWithdrawSbpModal } = useWithdrawSbpModals();
  const { openWithdrawSccrsModal } = useWithdrawSccrsModals();

  const isVerified = session?.additional?.isVerified || false;

  const { depositWithdrawAssetsData } = useDepositWithdrawAssetsDictionary();

  const showOperationModal = (
    component: ReactNode,
    options?: Partial<DialogComponentOptions>
  ) => {
    showDialogComponent({
      component,
      className: 'border-0',
      ...options,
    });
  };

  const openWithMethodData = (
    methodKey: string,
    translationKey: string,
    open: () => void,
    assetCode?: string
  ) => {
    if (
      hasOperationMethodData(
        depositWithdrawAssetsData?.value,
        methodKey,
        assetCode
      )
    ) {
      open();
      return;
    }

    showOperationModal(
      <OperationMethodDataGate
        title={t(`${translationKey}.title`)}
        methodKey={methodKey}
        assetCode={assetCode}
        onReady={open}
      />
    );
  };

  const showInstructionModal = (depositMethod: 'oplati' | 'erip') => {
    const methodConfig = {
      oplati: {
        depositMethod: 'oplati' as const,
        title: 'depositMethods.oplati.title',
      },
      erip: {
        depositMethod: 'erip' as const,
        title: 'depositMethods.erip.title',
        className: 'h-[8.4rem] w-[8.4rem]',
      },
    };

    const config = methodConfig[depositMethod];

    showOperationModal(
      <DepositInstructionModal
        onBack={() => openOperationMethodsModal()}
        onClose={closeDialog}
        title={config.title}
        depositMethod={config.depositMethod}
      />
    );
  };

  const showCryptoModal = (assetCode?: string) => {
    showOperationModal(
      <DepositCryptoModal
        assetCode={assetCode}
        onBack={openOperationMethodsModal}
        onClose={closeDialog}
        title={t('depositMethods.crypto.title')}
      />
    );
  };

  const showBankTransferModal = () => {
    showOperationModal(
      <DepositBankTransferModal
        onBack={() => openOperationMethodsModal()}
        onClose={closeDialog}
        title={t('depositMethods.bankTransfer.title')}
      />
    );
  };

  const showCardDepositModal = (assetCode?: string) => {
    showOperationModal(
      <DepositCardModal
        assetCode={assetCode}
        onBack={() => openOperationMethodsModal()}
        onClose={closeDialog}
        title={t('depositMethods.creditCard.title')}
      />
    );
  };

  const showSbpDepositModal = () => {
    showOperationModal(
      <DepositSbpModal
        onBack={() => openOperationMethodsModal()}
        title={t('depositMethods.sbp.title')}
      />
    );
  };

  const showSberpayDepositModal = () => {
    showOperationModal(
      <DepositSberpayModal
        onBack={() => openOperationMethodsModal()}
        title={t('depositMethods.sberpay.title')}
      />
    );
  };

  const showSccrsDepositModal = () => {
    showOperationModal(
      <DepositSccrsModal
        onBack={() => openOperationMethodsModal()}
        title={t('depositMethods.sccrs.title')}
      />
    );
  };

  const setOnWithdrawFormBack = () => {
    useModalStore
      .getState()
      .setState('onWithdrawFormBack', openOperationMethodsModal);
  };

  const showWithdrawCryptoModal = (assetCode?: string) => {
    setOnWithdrawFormBack();
    openWithdrawFormModal(assetCode);
  };

  const showWithdrawCardModal = (assetCode?: string) => {
    setOnWithdrawFormBack();
    openWithdrawCardModal(assetCode);
  };

  const showWithdrawBankTransferModal = (assetCode?: string) => {
    setOnWithdrawFormBack();
    openWithdrawBankTransferModal(assetCode);
  };

  const showWithdrawSbpModal = () => {
    setOnWithdrawFormBack();
    openWithdrawSbpModal();
  };

  const showSccrsWithdrawModal = () => {
    setOnWithdrawFormBack();
    openWithdrawSccrsModal();
  };

  const handleMethodSelect = useCallback(
    (method: OperationMethod, assetCode?: string) => {
      if (operationType === 'withdraw') {
        switch (method.key) {
          case 'withdraw.crypto':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showWithdrawCryptoModal(assetCode),
              assetCode
            );
            break;
          case 'withdraw.card':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showWithdrawCardModal(assetCode),
              assetCode
            );
            break;
          case 'withdraw.bank':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showWithdrawBankTransferModal(assetCode),
              assetCode
            );
            break;
          case 'withdraw.sbp':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showWithdrawSbpModal(),
              assetCode
            );
            break;
          case 'withdraw.sccrs':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showSccrsWithdrawModal(),
              assetCode
            );
            break;
          default: {
            const onMethodSelect = customOnMethodSelect || closeDialog;
            onMethodSelect(method);
            break;
          }
        }
        return;
      }

      if (operationType === 'deposit') {
        switch (method.key) {
          case 'deposit.erip':
            showInstructionModal('erip');
            break;
          case 'deposit.oplati':
            showInstructionModal('oplati');
            break;
          case 'deposit.crypto':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showCryptoModal(assetCode),
              assetCode
            );
            break;
          case 'deposit.bank':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showBankTransferModal(),
              assetCode
            );
            break;
          case 'deposit.card':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showCardDepositModal(assetCode),
              assetCode
            );
            break;
          case 'deposit.sbp':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showSbpDepositModal(),
              assetCode
            );
            break;
          case 'deposit.sberpay':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showSberpayDepositModal(),
              assetCode
            );
            break;
          case 'deposit.sccrs':
            openWithMethodData(
              method.key,
              method.translationKey,
              () => showSccrsDepositModal(),
              assetCode
            );
            break;
          default: {
            const onMethodSelect = customOnMethodSelect || closeDialog;
            onMethodSelect(method);
            break;
          }
        }
      }
    },
    [
      operationType,
      customOnMethodSelect,
      closeDialog,
      openWithMethodData,
      showWithdrawCryptoModal,
      showWithdrawCardModal,
      showWithdrawBankTransferModal,
      showWithdrawSbpModal,
      showInstructionModal,
      showCryptoModal,
      showBankTransferModal,
      showCardDepositModal,
      showSbpDepositModal,
      showSberpayDepositModal,
    ]
  );

  const openOperationMethodsModal: (
    props?: OpenOperationMethodsModalProps
  ) => void = props => {
    const onMethodSelect = props?.customOnMethodSelect || handleMethodSelect;

    if (!isVerified) {
      openVerificationInfoModal();
      return;
    }

    if (props?.assetGroup && props.assetGroup === 'Crypto') {
      if (operationType === 'withdraw') {
        return openWithMethodData(
          'withdraw.crypto',
          'withdrawMethods.crypto',
          () => showWithdrawCryptoModal(props?.assetCode),
          props?.assetCode
        );
      } else if (operationType === 'deposit') {
        return openWithMethodData(
          'deposit.crypto',
          'depositMethods.crypto',
          () => showCryptoModal(props?.assetCode),
          props?.assetCode
        );
      }
    }

    showDialogComponent({
      component: (
        <OperationMethodsModal
          assetCode={props?.assetCode}
          assetGroup={props?.assetGroup}
          operationType={operationType}
          onMethodSelect={onMethodSelect}
        />
      ),
      className:
        modalClassName ||
        'sm:max-w-[48rem] max-h-[90vh] overflow-y-auto border-0 flex bg-secondary-bg',
      onClose: props?.onClose || closeDialog,
    });
  };

  return {
    openOperationMethodsModal,
    closeDialog,
    showInstructionModal,
    showBankTransferModal,
    showSbpDepositModal,
    showSberpayDepositModal,
    showWithdrawCryptoModal,
  };
}

export function useDepositMethods() {
  return useOperationMethods({ operationType: 'deposit' });
}

export function useWithdrawMethods() {
  return useOperationMethods({ operationType: 'withdraw' });
}

export function useAllOperationMethods() {
  const depositMethods = useDepositMethods();
  const withdrawMethods = useWithdrawMethods();

  return {
    depositMethods,
    withdrawMethods,
  };
}
